import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useUser } from "@/hooks/useUser";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { Link, useMatch } from "react-router";
import { LucideIcon, LucideIconName } from "../LucideIcon";
import { getFilteredMenuItemsByFirm, tryGetTranslatedMenuName } from "./NavMenuUtils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronRight } from "lucide-react";
import _ from "lodash";

export const NavMenu = () => {
  const { isMobile, toggleSidebar } = useSidebar();
  const { user } = useUser();
  const menuArchitecture = user?.menuArchitecture || [];
  const activeFirm = useSelector(selectActiveFirm);
  const menuItems = user?.menuRights || [];
  const currentDetailedEntityPath =
    window.location.pathname.includes("Detail") && window.location.pathname.split("Detail")[0];
  const currentAddEntityPath = window.location.pathname.includes("Add") && window.location.pathname.split("Add")[0];

  const items: {
    title: string;
    url: string;
    icon?: any;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
      iconName?: string;
    }[];
  }[] = menuArchitecture
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      title: item.menuHeaderName,
      url: "",
      // icon: Menu,
      isActive: false,
      items: item.menuHeaderMenuList
        .map((r) => r.menuId)
        .map((r) => getFilteredMenuItemsByFirm(menuItems, activeFirm).find((menu) => menu.menuId === r))
        .filter((item) => item !== undefined)
        .map((menuItem) => ({
          title: menuItem?.menuName || "",
          url: menuItem?.path || "",
          iconName: menuItem?.menuIcon || "",
        }))
        .filter((item) => typeof item.url === "string"),
    }))
    .filter((r) => r.items.length)
    .map((r) => {
      r.items = _.uniqBy(r.items, "title");
      return r;
    });

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <SidebarMenu>
        {items.map((item) => {
          if (item.items && item.items.length === 1) {
            const subItem = item.items[0];
            const isActive =
              useMatch(subItem.url) ||
              currentDetailedEntityPath === subItem.url ||
              currentAddEntityPath === subItem.url;

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  tooltip={tryGetTranslatedMenuName(subItem.title)}
                  className="hover:shadow-sm"
                >
                  <Link
                    to={subItem.url}
                    onClick={() => {
                      if (isMobile) {
                        toggleSidebar();
                      }
                    }}
                    className={isActive ? "font-bold bg-sidebar-accent/20 border border-border transition-all" : ""}
                  >
                    {/* <span className="bg-accent/80 rounded-full p-1">
                      <LucideIcon
                        name={subItem.iconName as LucideIconName}
                        className="stroke-primary w-4 h-4"
                      />
                    </span> */}
                    <span>{tryGetTranslatedMenuName(subItem.title)}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          }

          return (
            <Collapsible key={item.title} asChild defaultOpen={item.isActive} className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={item.title} className="hover:shadow-sm">
                    {item.icon && <item.icon />}
                    <span>{tryGetTranslatedMenuName(item.title)}</span>
                    <ChevronRight className="ml-auto transition-transform duration-100 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items?.map((subItem) => {
                      const isActive =
                        useMatch(subItem.url) ||
                        currentDetailedEntityPath === subItem.url ||
                        currentAddEntityPath === subItem.url;

                      return (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton asChild>
                            <Link
                              to={subItem.url}
                              onClick={() => {
                                if (isMobile) {
                                  toggleSidebar();
                                }
                              }}
                              className={
                                isActive
                                  ? "font-medium bg-sidebar-accent/20 text-primary-foreground font-bold border border-border transition-all"
                                  : ""
                              }
                            >
                              <span className="bg-accent/80 rounded-full p-1">
                                <LucideIcon
                                  name={subItem.iconName as LucideIconName}
                                  className="stroke-primary w-4 h-4"
                                />
                              </span>
                              <span>{tryGetTranslatedMenuName(subItem.title)}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
};
